import { useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { AIAssistedType } from "../types/gino";
import { getAssistantId } from "../utils/openAI";
import { updateGinoTokens } from "../api/services/ginoCredits";
import { useSendMessage } from "../api/mutations/useSendMessage";
import {
  createThread,
  fetchLatestMessage,
  runAssistant,
  sendMessageToThread,
  waitForRunCompletion,
} from "../api/services/openAI";

interface SendMessagesToGinoProps {
  userId: number | null;
  chatId?: number | null;
}

export const useSendMessagesToGinoUpdated = ({ userId, chatId }: SendMessagesToGinoProps) => {
  const chatInputRef = useRef<HTMLInputElement>(null);
  const [openAIThreadId, setOpenAIThreadId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [searchParams] = useSearchParams();
  const about = searchParams.get("about") as AIAssistedType;
  const openAIAssistantId = getAssistantId(about);

  const { mutate: sendMessage, isPending: isSendingMessage } = useSendMessage(chatId);

  const processOpenAIThread = async (threadId: string, text: string) => {
    if (!threadId) return;

    await sendMessageToThread(threadId, text);

    const runId = await runAssistant(threadId, openAIAssistantId);
    const runResult = await waitForRunCompletion(threadId, runId);

    const latestMessage = await fetchLatestMessage(threadId);
    const assistantText = latestMessage?.text?.value;

    const tokenRequest = {
      // Old flow:
      // message: `user: ${text}\nassistant: ${latestMessage}`,
      message: `user: ${text}\nassistant: ${assistantText ?? ""}`,
      assistantId: openAIAssistantId,
      prompt_tokens: runResult?.usage?.prompt_tokens ?? 0,
      completion_tokens: runResult?.usage?.completion_tokens ?? 0,
      total_tokens: runResult?.usage?.total_tokens ?? 0,
    };

    await updateGinoTokens(tokenRequest);

    return latestMessage;
  };

  const sendMessageToOpenAI = async (text: string) => {
    try {
      if (!openAIThreadId) {
        const id = await createThread();
        setOpenAIThreadId(id);
        return await processOpenAIThread(id, text);
      }
      return await processOpenAIThread(openAIThreadId, text);
    } catch (err) {
      throw new Error("Failed to communicate with OpenAI", { cause: err });
    }
  };

  const sendTextMessage = async (text: string) => {
    if (!userId) {
      throw new Error("Missing userId");
    }

    setIsLoading(true);
    setError(null);

    try {
      sendMessage({
        room: Number(chatId!),
        type: "text",
        text: text,
        isGino: false,
      });

      const latestMessage = await sendMessageToOpenAI(text);

      if (!latestMessage) return;

      sendMessage({
        room: Number(chatId!),
        type: "text",
        text: latestMessage.text.value,
        isGino: true,
      });
    } catch (err) {
      throw new Error(err instanceof Error ? err.message : "Failed to process message");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendTextMessage = async (message?: string) => {
    if (!message) return;

    try {
      await sendTextMessage(message);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send message");
    }
  };

  const handleOnKeyDownWithinChatInput = async (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      await handleSendTextMessage();
    }
  };

  return {
    chatInputRef,
    handleSendTextMessage,
    handleOnKeyDownWithinChatInput,
    isLoading: isLoading || isSendingMessage,
    error,
  };
};
